/**
 * Waits for a custom element to be defined and all instances to be connected.
 * @param {string} tag - The custom element tag name to wait for.
 * @param {number} [timeout=10000] - Maximum time in milliseconds to wait before timing out.
 * @returns {Promise<void>} A promise that resolves when the element is defined and all instances are connected.
 * @throws {Error} If the element is not defined or connected within the timeout period.
 */
export async function whenConnected(tag, timeout = 10000) {
    const start = performance.now();

    // Wait for the custom element to be defined
    await Promise.race([
        customElements.whenDefined(tag),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`Timed out waiting for ${tag} to be defined`)), timeout),
        ),
    ]);

    // Wait until none of the existing elements are missing the connected state
    return new Promise((resolve, reject) => {
        (function check() {
            if (!document.querySelector(`${tag}:not(:state(connected))`)) return resolve();
            if (timeout != null && performance.now() - start > timeout) {
                return reject(new Error(`Timed out waiting for ${tag}:state(connected)`));
            }
            requestAnimationFrame(check);
        })();
    });
}
